import { useCallback, useLayoutEffect, useRef } from 'react';

type Handler<TArgs extends unknown[], TReturn> = (...args: TArgs) => TReturn;

/**
 * Half-arsed version of proposed useEvent hook (it's been 3 years)
 *
 * Use it to stabilize a handler that doesn't make sense to include in the dependency array of an effect or event handler. Dropping the dependency by itself wll just lead to stale values.
 *
 * Examples:
 * - it's not because some dependencies of `onMessage` change, that you'd want to resubscribe to the message
 * - a debounce handler being redeclared doesn't mean the timeout instance needs to be recreated
 *
 * do NOT call returned handler during render
 *
 * @see [RFCS useEvent](https://github.com/reactjs/rfcs/blob/useevent/text/0000-useevent.md)
 */
const useEvent = <TArgs extends unknown[], TReturn = void>(
  handler: Handler<TArgs, TReturn> | undefined,
) => {
  const handlerRef = useRef<Handler<TArgs, TReturn> | null>(null);

  // avoids brief stale window I suppose, unlike a regular effect which runs after painting
  useLayoutEffect(() => {
    if (!handler) return;

    handlerRef.current = handler;
  });

  return useCallback((...args: TArgs) => handlerRef.current?.(...args), []);
};

export default useEvent;
